import { useCallback, useMemo } from 'react'

import { useExtDataIntegrations } from 'data/hooks/dataConnections/useExtDataIntegrations'
import { useObjects, useSharedWorkspaceObjects } from 'data/hooks/objects'
import { importSharedObject } from 'data/hooks/objects/objectOperations'
import { refetchObjects } from 'data/hooks/objects/refetchObjects'
import useLDFlags from 'data/hooks/useLDFlags'
import { useNewNativeTableModal } from 'features/admin/data-connector/editors/native-tables/useNewNativeTableModal'
import { useNewDataConnectionModal } from 'features/DataConnections/useNewDataConnectionModal'

import { useToast } from 'ui/components/Toast'

type UseManageDataAddOrImportDropdownStateProps = {
    onSelectObject: (objectId: string) => void
}

export function useManageDataAddOrImportDropdownState({
    onSelectObject,
}: UseManageDataAddOrImportDropdownStateProps) {
    const { flags } = useLDFlags()
    const toast = useToast()

    const { show: showNewTableModal } = useNewNativeTableModal()
    const onAddTable = useCallback(() => {
        showNewTableModal({
            onObjectCreated: (_tableName: string, objectId: string) => {
                if (objectId) {
                    onSelectObject(objectId)
                }
            },
        })
    }, [showNewTableModal, onSelectObject])

    const { data: objects = [] } = useObjects()
    const { data: workspaceObjects } = useSharedWorkspaceObjects()
    const sharedObjects = useMemo(() => {
        if (!workspaceObjects || !objects || !flags.sharedTables) return []

        const localObjectSids = new Set(objects.map((obj) => obj._sid))
        return workspaceObjects.filter((obj) => !localObjectSids.has(obj._sid))
    }, [workspaceObjects, objects, flags.sharedTables])

    const onSharedTableSelect = async (workspaceObject: ObjectDto) => {
        try {
            const importedObject = await importSharedObject(workspaceObject._sid)
            if (!importedObject) return Promise.resolve()

            await refetchObjects()
            onSelectObject(importedObject?._sid)
        } catch (resp) {
            console.error(resp)

            toast({
                type: 'error',
                title: 'There was a problem importing the table',
                startIcon: { name: 'AlertCircle' },
                helperText:
                    resp instanceof Response && 'status' in resp && resp.status === 400
                        ? 'A table with this name already exists in this app.'
                        : 'Please try again later. If the issue persists, contact support.',
            })
        }
    }

    const { data: integrationsData } = useExtDataIntegrations()
    const dataSyncIntegrations = integrationsData?.integrations ?? []

    const { show: showNewDataConnectionModal } = useNewDataConnectionModal()
    const onIntegrationSelect = (integration: ExtDataIntegrationsDto) => {
        showNewDataConnectionModal({
            initialIntegrationId: integration.id,
            initialSelectedExternalAccountId: '',
        })
    }

    return {
        dataSyncIntegrations,
        sharedObjects,
        onAddTable,
        onIntegrationSelect,
        onSharedTableSelect,
    }
}
